/* eslint-disable camelcase */
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { PureTranslator } from 'ggApp/modules/translator/components';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { InputV2, ButtonV2, Hint } from '@getgrover/ui';

import { formatZipcode } from 'ggApp/shared/context/Order/helpers/countryHelpers';
import { fieldName, defaultValues } from './defaultAddress';
import { validate } from './fieldValidation';

import { Form, FormBody, InputField, Actions, Error, BtnsWrapper, Btns, BackBtn } from './styles';

export const AddressComponent = ({
    shouldShowHint = false,
    stepData,
    shouldShowBackBtn,
    address,
    send,
    current,
}) => {
    const { countryId, countryName } = useContext(OrderContext);

    const [animate, setAnimate] = useState(false);

    const currentContext = useMemo(() => {
        if (!!address && typeof address === 'object' && Object.keys(address).length > 0) {
            return {
                fid: address.id,
                ...address,
            };
        }

        const initial = defaultValues.reduce((o, key) => {
            let val = '';

            if (key.name === fieldName.COUNTRY_NAME) {
                val = countryName;
            } else if (key.name === fieldName.COUNTRY_ID) {
                val = countryId;
            }

            return Object.assign(o, {
                [key.name]: val,
            });
        }, {});

        return initial;
    }, [countryName, countryId, address]);

    const { setError, errors, control, getValues, setValue, handleSubmit, formState } = useForm({
        validate,
        mode: 'onChange',
        defaultValues: currentContext,
    });

    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    useEffect(
        () =>
            setIsBtnDisabled(
                !Object.values(control.fieldsRef.current).every((i) =>
                    i.required ? !!getValues(i.ref.name) : true,
                ),
            ),
        [control, getValues, setIsBtnDisabled],
    );

    useEffect(() => {
        if (current.context.addressError) {
            setError('id', 'error', current.context.addressError ?? 'Error');
        }
    }, [setError, current]);

    useEffect(() => {
        const zipVal = getValues('zipcode');
        if (zipVal) {
            setValue('zipcode', formatZipcode(zipVal, countryId));
        }
    }, [countryId, setValue, getValues, formState]);

    const handleFormSubmit = useCallback(
        (data) => {
            const payload = {
                additional_info: data.additional_info,
                address1: data.address1,
                address2: data.address2,
                city: data.city,
                country_id: data.country_id,
                country_name: data.country_name,
                zipcode: formatZipcode(data.zipcode, countryId),
            };

            if (!!current.context.addressError) {
                send('SET_ADDRESS_ERROR');
            }

            return data.fid
                ? send('UPDATE', { address: { id: data.fid, ...payload } })
                : send('CREATE', { address: payload });
        },
        [current, send, countryId],
    );

    const onBackClick = useCallback(() => {
        setAnimate(true);
        setTimeout(() => send('BACK'), 400);
    }, [send]);

    return (
        <Form
            animate={animate}
            data-testid="address-form"
            autoComplete="on"
            onSubmit={handleSubmit(handleFormSubmit)}
        >
            <FormBody>
                {defaultValues.map((field) => {
                    const readOnly =
                        field.name === fieldName.ID || field.name === fieldName.COUNTRY_ID;

                    return (
                        <InputField key={field.name} area={readOnly ? 'none' : field.name}>
                            <Controller
                                key={`field-${field.name}`}
                                name={field.name}
                                control={control}
                                as={
                                    <InputV2
                                        data-testid={`form-${field.name}`}
                                        autoComplete="on"
                                        label={<PureTranslator tk={field.tk || ''} />}
                                        className={
                                            formState.isSubmitted && errors[field.name]
                                                ? 'error'
                                                : ''
                                        }
                                        isDisabled={
                                            readOnly || field.name === fieldName.COUNTRY_NAME
                                        }
                                    />
                                }
                                rules={{
                                    required: field.isReq && 'NEW_CHECKOUT_SHIPPING_ERROR_EMPTY',
                                    validate: () =>
                                        validate(field.name, control.getValues(field.name)),
                                }}
                            />
                        </InputField>
                    );
                })}
            </FormBody>

            <Actions>
                {Object.keys(errors).length > 0 && (
                    <Error data-testid="address-form-error">
                        <PureTranslator tk={errors[Object.keys(errors)[0]].message} />
                    </Error>
                )}

                <BtnsWrapper>
                    {shouldShowHint && <Hint text={<PureTranslator tk={stepData.subTitle} />} />}

                    <Btns>
                        {shouldShowBackBtn && (
                            <BackBtn
                                data-testid="address-form-back"
                                outlined
                                type="button"
                                onClick={onBackClick}
                            >
                                <PureTranslator tk="NEW_CHECKOUT_HOME_BACK_BTN" />
                            </BackBtn>
                        )}

                        <ButtonV2
                            data-testid="address-form-submit"
                            fullWidth
                            type="submit"
                            isDisabled={isBtnDisabled}
                        >
                            <PureTranslator tk={stepData.ctaContent} />
                        </ButtonV2>
                    </Btns>
                </BtnsWrapper>
            </Actions>
        </Form>
    );
};
